import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  CircularProgress,
  useTheme,
  alpha,
  Paper,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  PlayArrow,
  Pause,
  Fullscreen,
  VolumeUp,
  VolumeOff,
  SkipNext,
  SkipPrevious,
  Replay,
  Lock,
  CheckCircle,
  Bookmark,
  BookmarkBorder,
  ChevronRight,
  ChevronLeft,
  ExpandMore,
  ExpandLess,
  School,
  EmojiEvents,
  History,
  ArrowBack,
  ArrowForward,
  PlayCircle,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";

// Constants
const API_BASE_URL = "https://tdtlworld.com/dss-kaas-backend";
const GOLD = "#D4AF37";

// --- Sub-components ---

const CustomVideoPlayer = ({ videoUrl, onEnd, onProgress }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      const progressValue = video.duration
        ? (video.currentTime / video.duration) * 100
        : 0;
      setProgress(progressValue);
      onProgress(progressValue);

      if (progressValue >= 99.5 && !hasReachedEnd) {
        setHasReachedEnd(true);
        onEnd();
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setHasReachedEnd(false);
    };

    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, [onEnd, onProgress, hasReachedEnd]);

  useEffect(() => {
    setHasReachedEnd(false);
  }, [videoUrl]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (isPlaying) video.pause();
    else video.play();
  };

  const handleProgressChange = (e) => {
    const value = parseFloat(e.target.value);
    const video = videoRef.current;
    video.currentTime = (value / 100) * (video.duration || 0);
    setProgress(value);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds < 0) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  return (
    <Box sx={{ position: "relative", width: "100%", aspectRatio: "16/9", bgcolor: "black", borderRadius: 2, overflow: "hidden" }}>
      <video
        ref={videoRef}
        src={videoUrl}
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
        autoPlay
        onContextMenu={(e) => e.preventDefault()}
      />
      <Box 
        sx={{ 
          position: "absolute", 
          bottom: 0, 
          left: 0, 
          right: 0, 
          p: 2, 
          background: "linear-gradient(transparent, rgba(0,0,0,0.8))",
          display: "flex",
          flexDirection: "column",
          gap: 1
        }}
      >
        <input
          type="range"
          min="0"
          max="100"
          step="0.1"
          value={progress}
          onChange={handleProgressChange}
          style={{ width: "100%", accentColor: GOLD }}
        />
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={togglePlay} sx={{ color: "white" }}>
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <Typography variant="caption" sx={{ color: "white" }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

// --- Main Component ---

export default function CourseViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const token = localStorage.getItem("jwttoken");

  // State
  const [course, setCourse] = useState(null);
  const [activePartIndex, setActivePartIndex] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInstructor, setInstructor] = useState(false);
  const [isEnrolled, setEnrolled] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submittedQuizzes, setSubmittedQuizzes] = useState([]);
  const [bookmarkedLessons, setBookmarkedLessons] = useState([]);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Initial Load
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axios.get(`${API_BASE_URL}/lms/courses/${id}/`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      setCourse(res.data);
      setInstructor(res.data.display.is_instructor);
      setEnrolled(res.data.display.is_enrolled);
      
      // Auto-select first incomplete lesson
      let initialLesson = null;
      let initialPartIdx = 0;
      if (res.data.parts) {
        for (let i = 0; i < res.data.parts.length; i++) {
          const incomplete = res.data.parts[i].subparts?.find(s => !s.is_complete);
          if (incomplete) { initialLesson = incomplete; initialPartIdx = i; break; }
        }
      }
      if (!initialLesson && res.data.parts?.[0]?.subparts?.[0]) {
        initialLesson = res.data.parts[0].subparts[0];
        initialPartIdx = 0;
      }
      if (initialLesson) {
        setActiveLesson({
          title: initialLesson.subpart_title,
          content: initialLesson.part_description || "No description available.",
          is_complete: initialLesson.is_complete,
          video_url: initialLesson.video || null,
          subpart_id: initialLesson.subpart_id
        });
        setActivePartIndex(initialPartIdx);
      }
    })
    .catch(err => {
      setError("Failed to load course details.");
      console.error(err);
    })
    .finally(() => setLoading(false));

    // Load extra metadata
    const savedQuizzes = localStorage.getItem(`submittedQuizzes-${id}`);
    if (savedQuizzes) setSubmittedQuizzes(JSON.parse(savedQuizzes));
    const savedBookmarks = localStorage.getItem(`bookmarks-${id}`);
    if (savedBookmarks) setBookmarkedLessons(JSON.parse(savedBookmarks));
  }, [id, token]);

  // Logic Handlers
  const isLessonLocked = (pIdx, sIdx) => {
    if (!course?.parts?.[pIdx]) return true;
    if (pIdx === 0 && sIdx === 0) return false;
    if (sIdx > 0) return !course.parts[pIdx].subparts[sIdx - 1].is_complete;
    if (pIdx > 0) {
      const prevPart = course.parts[pIdx - 1];
      return !prevPart.subparts?.every(s => s.is_complete);
    }
    return true;
  };

  const handleLessonSelect = (pIdx, subpartId) => {
    const part = course.parts[pIdx];
    const sIdx = part.subparts.findIndex(s => s.subpart_id === subpartId);
    if (sIdx === -1) return;
    
    if (isLessonLocked(pIdx, sIdx)) {
      Swal.fire({ title: "Locked", text: "Please complete the previous lesson first.", icon: "warning", confirmButtonColor: GOLD });
      return;
    }

    const lesson = part.subparts[sIdx];
    setActiveLesson({
      title: lesson.subpart_title,
      content: lesson.part_description || "No description available.",
      is_complete: lesson.is_complete,
      video_url: lesson.video || null,
      subpart_id: lesson.subpart_id
    });
    setActivePartIndex(pIdx);
    setQuiz(null);
    setShowMobileSidebar(false);
  };

  const handleVideoEnd = () => {
    if (!activeLesson || activeLesson.is_complete) return;
    
    // Optimistic update
    setActiveLesson(prev => ({ ...prev, is_complete: true }));
    
    // API update
    axios.patch(`${API_BASE_URL}/lms/subparts/${activeLesson.subpart_id}/`, 
      { is_complete: true },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    Swal.fire({
      title: "Lesson Complete!",
      text: "You've unlocked the next lesson or part quiz.",
      icon: "success",
      confirmButtonText: "Great!",
      confirmButtonColor: GOLD
    });
  };

  const fetchQuiz = () => {
    if (!activeLesson) return;
    setLoading(true);
    axios.get(`${API_BASE_URL}/quiz/quiz-by-coursepart/${activeLesson.subpart_id}/`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      setQuiz(res.data);
      setShowQuizModal(true);
    })
    .catch(() => Swal.fire("Error", "Could not load quiz.", "error"))
    .finally(() => setLoading(false));
  };

  const submitQuiz = async () => {
    const payload = {
      quiz: quiz.id,
      answers: Object.entries(answers).map(([qid, oid]) => ({ question: parseInt(qid), selected_option: oid }))
    };
    try {
      const res = await axios.post(`${API_BASE_URL}/quiz/quiz-submit/`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuizResults(res.data);
      if (res.data.result === "Passed") {
        setSubmittedQuizzes(prev => [...prev, activeLesson.subpart_id]);
      }
    } catch (err) {
      Swal.fire("Error", "Failed to submit quiz.", "error");
    }
  };

  if (loading && !course) return <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh" }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ py: 8 }}><Alert severity="error">{error}</Alert></Container>;
  if (!course) return null;

  const totalLessons = course.parts?.reduce((acc, p) => acc + (p.subparts?.length || 0), 0) || 0;
  const completedLessons = course.parts?.reduce((acc, p) => acc + (p.subparts?.filter(s => s.is_complete).length || 0), 0) || 0;
  const progress = totalLessons ? (completedLessons / totalLessons) * 100 : 0;

  return (
    <Box sx={{ bgcolor: "background.paper", borderRadius: 4, overflow: "hidden" }}>
      <Grid container>
        {/* Main Learning Area */}
        <Grid item xs={12} lg={8.5} sx={{ borderRight: { lg: "1px solid" }, borderColor: "divider" }}>
          {activeLesson ? (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <CustomVideoPlayer 
                videoUrl={activeLesson.video_url} 
                onEnd={handleVideoEnd} 
                onProgress={() => {}} 
              />
              
              <Box sx={{ mt: 4, px: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h5" 
                      fontWeight={900} 
                      sx={{ letterSpacing: "-0.5px", mb: 1, color: "text.primary" }}
                    >
                      {activeLesson.title}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="text.secondary" 
                      sx={{ lineHeight: 1.6, maxWidth: "90%", fontWeight: 500 }}
                    >
                      {activeLesson.content}
                    </Typography>
                  </Box>
                  <Tooltip title="Bookmark Lesson">
                    <IconButton 
                      onClick={() => {
                        const newBookmarks = bookmarkedLessons.includes(activeLesson.subpart_id) 
                          ? bookmarkedLessons.filter(b => b !== activeLesson.subpart_id)
                          : [...bookmarkedLessons, activeLesson.subpart_id];
                        setBookmarkedLessons(newBookmarks);
                        localStorage.setItem(`bookmarks-${id}`, JSON.stringify(newBookmarks));
                      }}
                      sx={{ bgcolor: alpha(GOLD, 0.05), "&:hover": { bgcolor: alpha(GOLD, 0.1) } }}
                    >
                      {bookmarkedLessons.includes(activeLesson.subpart_id) ? <Bookmark sx={{ color: GOLD }} /> : <BookmarkBorder />}
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Divider sx={{ my: 4 }} />

                <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                  <Button 
                    variant="outlined" 
                    startIcon={<ArrowBack />}
                    onClick={() => {
                      // Logic for previous lesson
                    }}
                    sx={{ borderRadius: "12px", px: 3, py: 1 }}
                  >
                    Previous
                  </Button>
                  
                  <Stack direction="row" spacing={2}>
                    <Button 
                      variant="contained" 
                      onClick={fetchQuiz}
                      disabled={!activeLesson.is_complete || submittedQuizzes.includes(activeLesson.subpart_id)}
                      sx={{ 
                        bgcolor: GOLD, 
                        fontWeight: 800,
                        borderRadius: "12px",
                        px: 3,
                        "&:hover": { bgcolor: alpha(GOLD, 0.8) } 
                      }}
                    >
                      {submittedQuizzes.includes(activeLesson.subpart_id) ? "Section Passed" : "Take Section Quiz"}
                    </Button>
                    <Button 
                      variant="contained" 
                      endIcon={<ArrowForward />}
                      onClick={() => {
                        // Logic for next lesson
                      }}
                      sx={{ borderRadius: "12px", px: 3 }}
                    >
                      Next Lesson
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 20, px: 4 }}>
              <PlayCircle sx={{ fontSize: 80, color: alpha(GOLD, 0.2), mb: 2 }} />
              <Typography variant="h5" color="text.secondary" fontWeight={800}>Ready to start learning?</Typography>
              <Typography variant="body2" color="text.disabled">Select a lesson from the menu to begin your journey.</Typography>
            </Box>
          )}
        </Grid>

        {/* Lesson Navigation Sidebar */}
        <Grid item xs={12} lg={3.5} sx={{ bgcolor: alpha(theme.palette.background.default, 0.4) }}>
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1, color: "text.primary" }}>Course Content</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={progress} 
                    sx={{ 
                      height: 6, 
                      borderRadius: 3, 
                      bgcolor: alpha(GOLD, 0.1), 
                      "& .MuiLinearProgress-bar": { bgcolor: GOLD } 
                    }} 
                  />
                </Box>
                <Typography variant="caption" fontWeight={900}>{Math.round(progress)}%</Typography>
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", maxHeight: "600px" }}>
              <List disablePadding>
                {course.parts.map((part, pIdx) => (
                  <Box key={part.part_id}>
                    <ListItemButton 
                      onClick={() => setActivePartIndex(activePartIndex === pIdx ? null : pIdx)}
                      sx={{ 
                        py: 2, 
                        borderBottom: "1px solid", 
                        borderColor: alpha(theme.palette.divider, 0.1),
                        bgcolor: activePartIndex === pIdx ? alpha(GOLD, 0.05) : "transparent"
                      }}
                    >
                      <ListItemText 
                        primary={part.part_title} 
                        primaryTypographyProps={{ fontWeight: 800, fontSize: "0.9rem" }} 
                      />
                      {activePartIndex === pIdx ? <ExpandLess sx={{ fontSize: 20 }} /> : <ExpandMore sx={{ fontSize: 20 }} />}
                    </ListItemButton>
                    <AnimatePresence>
                      {activePartIndex === pIdx && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <List disablePadding>
                            {part.subparts.map((lesson, sIdx) => {
                              const locked = isLessonLocked(pIdx, sIdx);
                              const active = activeLesson?.subpart_id === lesson.subpart_id;
                              return (
                                <ListItem key={lesson.subpart_id} disablePadding>
                                  <ListItemButton 
                                    selected={active}
                                    disabled={locked}
                                    onClick={() => handleLessonSelect(pIdx, lesson.subpart_id)}
                                    sx={{ 
                                      pl: 4, 
                                      py: 1.5,
                                      transition: "0.2s",
                                      borderLeft: "3px solid",
                                      borderColor: active ? GOLD : "transparent",
                                      "&.Mui-selected": {
                                        bgcolor: alpha(GOLD, 0.1),
                                        "&:hover": { bgcolor: alpha(GOLD, 0.15) }
                                      }
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      {lesson.is_complete ? (
                                        <CheckCircle sx={{ color: "success.main", fontSize: 18 }} />
                                      ) : locked ? (
                                        <Lock sx={{ fontSize: 18, color: "text.disabled" }} />
                                      ) : (
                                        <PlayArrow sx={{ fontSize: 18, color: active ? GOLD : "text.secondary" }} />
                                      )}
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={lesson.subpart_title} 
                                      primaryTypographyProps={{ 
                                        fontSize: "0.85rem", 
                                        fontWeight: active ? 700 : 500,
                                        color: locked ? "text.disabled" : "text.primary" 
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>
                ))}
              </List>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Quiz Modal */}
      <Dialog 
        open={showQuizModal} 
        onClose={() => !quizResults && setShowQuizModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <DialogTitle sx={{ bgcolor: GOLD, color: "white", p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <EmojiEvents />
            <Typography variant="h6" fontWeight={900}>Knowledge Check</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 4, mt: 2 }}>
          {quizResults ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h4" fontWeight={900} color={quizResults.result === "Passed" ? "success.main" : "warning.main"}>
                {quizResults.result === "Passed" ? "Excellent Work!" : "Try Once More"}
              </Typography>
              <Typography variant="h1" fontWeight={900} sx={{ my: 3, color: GOLD }}>{Math.round(quizResults.score)}%</Typography>
              <Typography variant="body1" color="text.secondary" fontWeight={600}>{quizResults.message}</Typography>
              <Button 
                variant="contained" 
                fullWidth
                onClick={() => setShowQuizModal(false)} 
                sx={{ mt: 5, py: 2, borderRadius: "50px", bgcolor: GOLD, fontWeight: 900 }}
              >
                CONTINUE COURSE
              </Button>
            </Box>
          ) : quiz ? (
            <Box>
              <Typography variant="caption" color={GOLD} fontWeight={900} sx={{ letterSpacing: 1 }}>QUESTION {currentQuestionIndex + 1} OF {quiz.questions?.length}</Typography>
              <Typography variant="h5" fontWeight={800} sx={{ mt: 1, mb: 4, lineHeight: 1.4, color: "text.primary" }}>{quiz.questions[currentQuestionIndex].text}</Typography>
              <Stack spacing={2}>
                {quiz.questions[currentQuestionIndex].options.map(opt => {
                  const isSelected = answers[quiz.questions[currentQuestionIndex].id] === opt.id;
                  return (
                    <Button
                      key={opt.id}
                      variant={isSelected ? "contained" : "outlined"}
                      onClick={() => setAnswers(prev => ({ ...prev, [quiz.questions[currentQuestionIndex].id]: opt.id }))}
                      fullWidth
                      sx={{ 
                        justifyContent: "flex-start", p: 2.5, borderRadius: "16px",
                        borderWidth: isSelected ? 0 : 2, borderColor: isSelected ? "transparent" : alpha(GOLD, 0.2),
                        bgcolor: isSelected ? GOLD : "transparent",
                        color: isSelected ? "white" : "text.primary",
                        "&:hover": { bgcolor: isSelected ? alpha(GOLD, 0.9) : alpha(GOLD, 0.05), borderColor: GOLD }
                      }}
                    >
                      <Typography variant="body1" fontWeight={700}>{opt.text}</Typography>
                    </Button>
                  );
                })}
              </Stack>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 10 }}><CircularProgress sx={{ color: GOLD }} /></Box>
          )}
        </DialogContent>
        {quiz && !quizResults && (
          <DialogActions sx={{ p: 4, pt: 0 }}>
            <Button disabled={currentQuestionIndex === 0} onClick={() => setCurrentQuestionIndex(prev => prev - 1)} sx={{ fontWeight: 800 }}>BACK</Button>
            {currentQuestionIndex < (quiz.questions?.length - 1) ? (
              <Button variant="contained" sx={{ bgcolor: GOLD, px: 4, borderRadius: "50px", fontWeight: 900 }} onClick={() => setCurrentQuestionIndex(prev => prev + 1)}>NEXT</Button>
            ) : (
              <Button 
                variant="contained" 
                sx={{ bgcolor: GOLD, px: 4, borderRadius: "50px", fontWeight: 900 }} 
                onClick={submitQuiz}
                disabled={Object.keys(answers).length < quiz.questions.length}
              >
                SUBMIT RESULTS
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
}
